import React, {useMemo, useState} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import moment from 'moment';
import { ToastContainer } from 'react-toastify';

import TitleComponent from '../../components/TitleComponent/TitleComponent';
import notifyToast from '../../components/Toast/Toast';
import { naloziIzvjestaj } from '../../api/fmkNalozi';
import "moment/locale/bs";

moment.locale("bs");
interface ApiResponse<T> {
    error: number;
    success: boolean;
    message: string;
    data: T;
}

type Izvjestaj = {
    BrojNaloga: string;
    nalogData: Nalog[];
};

type Nalog = {
    Id: number;
    BrojNaloga: string;
    Pocetak: string | null;
    Kraj: string | null;
    Pauziran: number | null; // 1=pauziran, 2=zavrsen
    stavke: Stavka[];
};

type Stavka = {
    Id: number;
    SifraArtikla: string;
    NazivArtikla: string;
    Norma: number | string;
    Kolicina: number | string;
    Jm: string; // note: key is "Jm" in your data
    PotrebnoVrijeme?: number | string | null;
    uposlenik?: { UposlenikImePrezime?: string } | null;
    sredstvo?: { SredstvoNaziv?: string } | null;
};
// Reuse same parsing logic for both formatting and diffs
const parseDateTime = (value?: string | null) => {
    if (!value) return null;
    const m = moment(
        value,
        ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', moment.ISO_8601],
        true
    );
    return m.isValid() ? m : null;
};

const formatDateTime = (value?: string | null) => {
    const m = parseDateTime(value);
    return m ? m.format('DD.MM.YYYY HH:mm') : '—';
};

const formatDurationHM = (totalMinutes: number) => {
    if (!Number.isFinite(totalMinutes)) return '—';
    const mins = Math.max(0, Math.round(totalMinutes));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const parts: string[] = [];
    if (h) parts.push(`${h} h`);
    if (m) parts.push(`${m} min`);
    return parts.length ? parts.join(' ') : '0 min';
};

// Sum PotrebnoVrijeme across ALL stavke in an izvjestaj -> minutes
const sumPotrebnoVrijemeMinutesAll = (
    izvjestaj: Izvjestaj,
    unit: 'minutes' | 'hours' = 'minutes'
) => {
    let total = 0;
    for (const n of izvjestaj.nalogData ?? []) {
        for (const s of n.stavke ?? []) {
            const raw = (s.PotrebnoVrijeme ?? 0).toString().replace(',', '.');
            const val = parseFloat(raw);
            if (!Number.isFinite(val)) continue;
            total += unit === 'hours' ? val * 60 : val;
        }
    }
    return total;
};

// Sum PotrebnoVrijeme for a single nalog -> minutes
const sumPotrebnoVrijemeMinutesForNalog = (
    nalog: Nalog,
    unit: 'minutes' | 'hours' = 'minutes'
) => {
    let total = 0;
    for (const s of nalog.stavke ?? []) {
        const raw = (s.PotrebnoVrijeme ?? 0).toString().replace(',', '.');
        const val = parseFloat(raw);
        if (!Number.isFinite(val)) continue;
        total += unit === 'hours' ? val * 60 : val;
    }
    return total;
};

// Sum (Kraj - Pocetak) across ALL nalozi -> minutes
const sumTrajanjeNalogMinutesAll = (izvjestaj: Izvjestaj) => {
    let total = 0;
    for (const n of izvjestaj.nalogData ?? []) {
        const start = parseDateTime(n.Pocetak);
        const end = parseDateTime(n.Kraj);
        if (start && end) total += Math.max(0, end.diff(start, 'minutes'));
    }
    return total;
};

// (Kraj - Pocetak) for a single nalog -> minutes
const trajanjeNalogMinutes = (nalog: Nalog) => {
    const start = parseDateTime(nalog.Pocetak);
    const end = parseDateTime(nalog.Kraj);
    return start && end ? Math.max(0, end.diff(start, 'minutes')) : 0;
};

const pauziranLabel = (p?: number | null) =>
    p === 1 ? 'Nalog pauziran' : p === 2 ? 'Nalog završen' : '';
const FMKNalogIzvjestaj: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [izvjestaj, setIzvjestaj] = useState<Izvjestaj | null>(null);
    const potrebnoUnit: 'minutes' | 'hours' = 'minutes';
    // Form
    const initialValues = { brojNaloga: '', stavka: '' };
    const validationSchema = Yup.object({
        brojNaloga: Yup.string().required('Broj Naloga is required'),
        stavka: Yup.string(),
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setLoading(true);
            try {
                const res: ApiResponse<Izvjestaj> = await naloziIzvjestaj(values);
                const payload = res?.data;
                if (!payload || !Array.isArray(payload.nalogData)) {
                    notifyToast(false, 'Neočekivan odgovor servera.');
                    return;
                }
                setIzvjestaj(payload);
            } catch (error: any) {
                console.error('Error: ', error);
                notifyToast(false, error?.response?.data?.message || 'Error occurred while fetching data.');
                setStatus(error);
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });
    const ukupnoPotrebnoMin = useMemo(
        () => (izvjestaj ? sumPotrebnoVrijemeMinutesAll(izvjestaj, potrebnoUnit) : 0),
        [izvjestaj, potrebnoUnit]
    );

    const ukupnoTrajanjeMin = useMemo(
        () => (izvjestaj ? sumTrajanjeNalogMinutesAll(izvjestaj) : 0),
        [izvjestaj]
    );

    return (
        <div className="card mb-5 mb-xl-10">
            <ToastContainer />
            <div className="card-header border-0 cursor-pointer" role="button">
                <div className="card-title m-0">
                    <TitleComponent name="Detaljan Izvještaj Naloga (bez panela)" />
                </div>
            </div>

            <div id="kt_account_profile_details" className="collapse show">
                <div className="card-body border-top p-9">
                    {!izvjestaj ? (
                        // ---------- FORM ----------
                        <div className="row">
                            <div className="col-12">
                                <form onSubmit={formik.handleSubmit} noValidate className="form">
                                    <div className="card-body border-top p-9">
                                        <div className="row">
                                            <div className="col-4 mb-6">
                                                <label>Broj Naloga</label>
                                                <input
                                                    type="text"
                                                    placeholder="Broj Naloga"
                                                    {...formik.getFieldProps('brojNaloga')}
                                                    className={clsx(
                                                        'form-control form-control-lg form-control-solid',
                                                        {
                                                            'is-invalid':
                                                                formik.touched.brojNaloga && formik.errors.brojNaloga,
                                                            'is-valid':
                                                                formik.touched.brojNaloga && !formik.errors.brojNaloga,
                                                        }
                                                    )}
                                                />
                                                {formik.touched.brojNaloga && formik.errors.brojNaloga ? (
                                                    <div className="invalid-feedback">{formik.errors.brojNaloga}</div>
                                                ) : null}
                                            </div>
                                            <div className="card-footer d-flex justify-content-end py-6 px-9">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={loading || formik.isSubmitting || !formik.isValid}
                                                >
                                                    {!loading && 'Potvrdi'}
                                                    {loading && (
                                                        <span className="indicator-progress" style={{ display: 'block' }}>
                                                            Please wait...{' '}
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    ) : (
                        <div className="izvjestaj-lista" style={{ display: "grid", gap: 24 }}>
                            <div className='alert alert-success ps-2 col-12 text-center'><h3>{izvjestaj.BrojNaloga}</h3></div>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="alert alert-primary mb-0">
                                        <div className="fw-semibold">Ukupno potrebno vrijeme (stavke). Vrijeme po normi</div>
                                        <div>
                                            {formatDurationHM(ukupnoPotrebnoMin)} <span className="text-muted">({ukupnoPotrebnoMin} min)</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="alert alert-info mb-0">
                                        <div className="fw-semibold">Ukupno trajanje (Kraj − Početak). Vrijeme izrade</div>
                                        <div>
                                            {formatDurationHM(ukupnoTrajanjeMin)} <span className="text-muted">({ukupnoTrajanjeMin} min)</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {izvjestaj.nalogData?.map((nalog) => (
                                <div
                                    key={nalog.Id}
                                    style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 12,
                                        padding: 16,
                                    }}
                                >
                                    {/* zaglavlje */}
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                                            gap: 12,
                                            marginBottom: 12,
                                        }}
                                    >
                                        <span>ID: {nalog.Id}</span>
                                        <span>{nalog.BrojNaloga}</span>
                                        <span>Pocetak rada: {formatDateTime(nalog.Pocetak)}</span>
                                        <span>Kraj rada: {formatDateTime(nalog.Kraj)}</span>
                                        <span>{pauziranLabel(nalog.Pauziran)}</span>
                                    </div>

                                    {/* stavke */}
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                        <tr>
                                            {[
                                                "Šifra artikla",
                                                "Naziv artikla",
                                                "Norma",
                                                "Količina",
                                                "JM",
                                                "Potrebno vrijeme",
                                                "Sredstvo",
                                                "Uposlenik",
                                            ].map((h) => (
                                                <th
                                                    key={h}
                                                    style={{
                                                        textAlign: "left",
                                                        borderBottom: "1px solid #e5e7eb",
                                                        padding: "8px 6px",
                                                    }}
                                                >
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {nalog.stavke?.length ? (
                                            nalog.stavke.map((s) => (
                                                <tr key={s.Id}>
                                                    <td style={{ padding: "8px 6px" }}>{s.SifraArtikla}</td>
                                                    <td style={{ padding: "8px 6px" }}>{s.NazivArtikla}</td>
                                                    <td style={{ padding: "8px 6px" }}>{s.Norma}</td>
                                                    <td style={{ padding: "8px 6px" }}>{s.Kolicina}</td>
                                                    <td style={{ padding: "8px 6px" }}>{s.Jm}</td>
                                                    <td style={{ padding: "8px 6px" }}>
                                                        {s.PotrebnoVrijeme ?? "—"}
                                                    </td>
                                                    <td style={{ padding: "8px 6px" }}>
                                                        {s.sredstvo?.SredstvoNaziv ?? "—"}
                                                    </td>
                                                    <td style={{ padding: "8px 6px" }}>
                                                        {s.uposlenik?.UposlenikImePrezime ?? "—"}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={7}
                                                    style={{ padding: "8px 6px", color: "#6b7280" }}
                                                >
                                                    Nema stavki.
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FMKNalogIzvjestaj;
